import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Breadcrumbs, Card, CardContent, Chip } from '@mui/material';
import {
  Assessment as AssessmentIcon,
  NavigateNext as NavigateNextIcon,
  DevicesOther as DevicesOtherIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import Button from '../../../components/Button';
import DateRangePicker from '../../../components/DateRangePicker';
import ExportButton from '../../../components/ExportButton';
import { useNotification } from '../../../components/Notification';
import Select from '../../../components/Select';
import Table from '../../../components/Table';
import { formatDateWithCulture, formatDateTimeWithCulture } from '../../../utils/dateUtils';
import {
  fetchInit,
  fetchDeviceReport,
  exportDeviceReportToExcel,
  exportDeviceReportToPdf,
  refreshDeviceData,
  selectDeviceReportData,
  selectDeviceReportUnits,
  selectDeviceReportDeviceStatuses,
  selectDeviceReportDeviceSettingTypes,
  selectDeviceReportConnectionStatuses,
  selectDeviceReportLoading,
  selectDeviceReportExportLoading,
  selectDeviceReportPdfExportLoading,
  selectDeviceReportRefreshLoading,
  selectDeviceReportError,
  selectDeviceReportSuccessMessage,
  selectDeviceReportFilterUnitId,
  selectDeviceReportFilterFromDate,
  selectDeviceReportFilterToDate,
  selectDeviceReportFilterTableDeviceTypes,
  selectDeviceReportFilterTableStatuses,
  selectDeviceReportFilterTableConnectionStatuses,
  selectDeviceReportFilterLastCommFromDate,
  selectDeviceReportFilterLastCommToDate,
  selectDeviceReportSortColumn,
  selectDeviceReportSortDirection,
  setFilterUnitId,
  setFilterFromDate,
  setFilterToDate,
  setFilterTableDeviceTypes,
  setFilterTableStatuses,
  setFilterTableConnectionStatuses,
  setFilterLastCommFromDate,
  setFilterLastCommToDate,
  setSortColumn,
  setSortDirection,
  clearFilters,
  clearTableFilters,
  clearError,
  clearSuccessMessage,
} from './slice';

function DeviceReport() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  const reportData = useSelector(selectDeviceReportData);
  const units = useSelector(selectDeviceReportUnits);
  const deviceStatuses = useSelector(selectDeviceReportDeviceStatuses);
  const deviceSettingTypes = useSelector(selectDeviceReportDeviceSettingTypes);
  const connectionStatuses = useSelector(selectDeviceReportConnectionStatuses);
  const loading = useSelector(selectDeviceReportLoading);
  const exportLoading = useSelector(selectDeviceReportExportLoading);
  const pdfExportLoading = useSelector(selectDeviceReportPdfExportLoading);
  const refreshLoading = useSelector(selectDeviceReportRefreshLoading);
  const error = useSelector(selectDeviceReportError);
  const successMessage = useSelector(selectDeviceReportSuccessMessage);
  // API Filters
  const filterUnitId = useSelector(selectDeviceReportFilterUnitId);
  const filterFromDate = useSelector(selectDeviceReportFilterFromDate);
  const filterToDate = useSelector(selectDeviceReportFilterToDate);
  // Local Table Filters
  const filterTableDeviceTypes = useSelector(selectDeviceReportFilterTableDeviceTypes);
  const filterTableStatuses = useSelector(selectDeviceReportFilterTableStatuses);
  const filterTableConnectionStatuses = useSelector(selectDeviceReportFilterTableConnectionStatuses);
  const filterLastCommFromDate = useSelector(selectDeviceReportFilterLastCommFromDate);
  const filterLastCommToDate = useSelector(selectDeviceReportFilterLastCommToDate);
  // Sorting
  const sortColumn = useSelector(selectDeviceReportSortColumn);
  const sortDirection = useSelector(selectDeviceReportSortDirection);

  // Helper to check if value is empty (null, undefined, or empty string - NOT 0)
  const isEmpty = (value) => value === '' || value === null || value === undefined;

  // Fetch init data on mount
  useEffect(() => {
    dispatch(fetchInit());
  }, [dispatch]);

  // Auto-select first unit and today's date after init completes
  useEffect(() => {
    if (units.length > 0 && isEmpty(filterUnitId)) {
      dispatch(setFilterUnitId(units[0].id));
    }
    if (isEmpty(filterFromDate)) {
      const today = new Date().toISOString().split('T')[0];
      dispatch(setFilterFromDate(today));
    }
    if (isEmpty(filterToDate)) {
      const today = new Date().toISOString().split('T')[0];
      dispatch(setFilterToDate(today));
    }
  }, [units, filterUnitId, filterFromDate, filterToDate, dispatch]);

  // Show error notification
  useEffect(() => {
    if (error) {
      showNotification(t(error), 'error');
      dispatch(clearError());
    }
  }, [error, dispatch, showNotification, t]);

  // Show success notification
  useEffect(() => {
    if (successMessage) {
      showNotification(t(successMessage), 'success');
      dispatch(clearSuccessMessage());
    }
  }, [successMessage, dispatch, showNotification, t]);

  // Local filtering and sorting logic for table data
  const filteredAndSortedData = useMemo(() => {
    if (!reportData?.dataDeviceInDay) return [];

    let data = [...reportData.dataDeviceInDay];

    // Filter by device types (multi-select)
    if (filterTableDeviceTypes.length > 0) {
      data = data.filter((item) => filterTableDeviceTypes.includes(item.deviceTypeId));
    }

    // Filter by statuses (multi-select)
    if (filterTableStatuses.length > 0) {
      data = data.filter((item) => filterTableStatuses.includes(item.status));
    }

    // Filter by connection statuses (multi-select)
    if (filterTableConnectionStatuses.length > 0) {
      data = data.filter((item) => filterTableConnectionStatuses.includes(item.connectionStatus));
    }

    // Filter by lastCommunicationTime date range
    if (filterLastCommFromDate) {
      const fromDate = new Date(filterLastCommFromDate);
      fromDate.setHours(0, 0, 0, 0);
      data = data.filter((item) => {
        if (!item.lastComunicationTime) return false;
        const itemDate = new Date(item.lastComunicationTime);
        return itemDate >= fromDate;
      });
    }

    if (filterLastCommToDate) {
      const toDate = new Date(filterLastCommToDate);
      toDate.setHours(23, 59, 59, 999);
      data = data.filter((item) => {
        if (!item.lastComunicationTime) return false;
        const itemDate = new Date(item.lastComunicationTime);
        return itemDate <= toDate;
      });
    }

    // Sorting
    if (sortColumn) {
      data.sort((a, b) => {
        let aValue = a[sortColumn];
        let bValue = b[sortColumn];

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';

        // Handle date sorting
        if (sortColumn === 'lastComunicationTime') {
          aValue = aValue ? new Date(aValue).getTime() : 0;
          bValue = bValue ? new Date(bValue).getTime() : 0;
        }

        // Handle numeric sorting
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }

        // Handle string sorting
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        if (sortDirection === 'asc') {
          return aStr.localeCompare(bStr);
        }
        return bStr.localeCompare(aStr);
      });
    }

    return data;
  }, [
    reportData?.dataDeviceInDay,
    filterTableDeviceTypes,
    filterTableStatuses,
    filterTableConnectionStatuses,
    filterLastCommFromDate,
    filterLastCommToDate,
    sortColumn,
    sortDirection,
  ]);

  // Handle sorting change
  const handleSortChange = (column, direction) => {
    dispatch(setSortColumn(column));
    dispatch(setSortDirection(direction));
  };

  // Handle clear table filters
  const handleClearTableFilters = () => {
    dispatch(clearTableFilters());
  };

  const handleSearch = () => {
    if (isEmpty(filterUnitId) || isEmpty(filterFromDate) || isEmpty(filterToDate)) {
      showNotification(t('reports.device.errors.selectFilters'), 'warning');
      return;
    }
    dispatch(fetchDeviceReport());
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  const handleExportExcel = () => {
    if (isEmpty(filterUnitId) || isEmpty(filterFromDate) || isEmpty(filterToDate)) {
      showNotification(t('reports.device.errors.selectFilters'), 'warning');
      return;
    }
    dispatch(exportDeviceReportToExcel());
  };

  const handleExportPdf = () => {
    if (isEmpty(filterUnitId) || isEmpty(filterFromDate) || isEmpty(filterToDate)) {
      showNotification(t('reports.device.errors.selectFilters'), 'warning');
      return;
    }
    dispatch(exportDeviceReportToPdf());
  };

  const handleRefreshData = () => {
    if (isEmpty(filterUnitId) || filterUnitId === 0 || filterUnitId === '0') {
      showNotification(t('reports.device.errors.selectUnitForRefresh'), 'warning');
      return;
    }
    dispatch(refreshDeviceData());
  };

  // Define dashboard statistics data
  const dashboardStats = reportData
    ? [
        {
          key: 'totalDeviceWaiting',
          label: t('reports.device.dashboard.totalDeviceWaiting'),
          value: reportData.totalDeviceWaiting || 0,
          icon: <DevicesOtherIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#ed6c02',
          bgColor: '#fff4e5',
        },
        {
          key: 'totalDeviceApproved',
          label: t('reports.device.dashboard.totalDeviceApproved'),
          value: reportData.totalDeviceApproved || 0,
          icon: <CheckCircleIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#2e7d32',
          bgColor: '#edf7ed',
        },
        {
          key: 'totalDeviceRejected',
          label: t('reports.device.dashboard.totalDeviceRejected'),
          value: reportData.totalDeviceRejected || 0,
          icon: <ErrorIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#d32f2f',
          bgColor: '#fdeded',
        },
        {
          key: 'totalDeviceActive',
          label: t('reports.device.dashboard.totalDeviceActive'),
          value: reportData.totalDeviceActive || 0,
          icon: <CheckCircleIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#0288d1',
          bgColor: '#e5f6fd',
        },
        {
          key: 'totalDeviceRevoked',
          label: t('reports.device.dashboard.totalDeviceRevoked'),
          value: reportData.totalDeviceRevoked || 0,
          icon: <ErrorIcon sx={{ fontSize: 48, opacity: 0.6 }} />,
          color: '#7b1fa2',
          bgColor: '#f3e5f5',
        },
      ]
    : null;

  // Table columns with sorting
  const columns = [
    {
      key: 'deviceAddress',
      label: t('reports.device.table.deviceAddress'),
      sortable: true,
    },
    {
      key: 'deviceName',
      label: t('reports.device.table.deviceName'),
      sortable: true,
    },
    {
      key: 'deviceTypeId',
      label: t('reports.device.table.deviceType'),
      sortable: true,
      render: (value) => {
        const deviceType = deviceSettingTypes.find((dt) => dt.id === value);
        return deviceType?.name || value;
      },
    },
    {
      key: 'totalTimeDisconnect',
      label: t('reports.device.table.totalTimeDisconnect'),
      sortable: true,
      render: (value) => `${value} ${t('common.seconds')}`,
    },
    {
      key: 'status',
      label: t('reports.device.table.status'),
      sortable: true,
      render: (value) => {
        const status = deviceStatuses.find((s) => s.id === value);
        const statusName = status?.name || value;
        // Color mapping: 0=Waiting(warning), 1=Approved(success), 2=Rejected(error), 3=Active(success), 4=Revoked(error)
        const colorMap = {
          0: 'warning',  // Waiting
          1: 'success',  // Approved
          2: 'error',    // Rejected
          3: 'success',  // Active
          4: 'error',    // Revoked
        };
        const chipColor = colorMap[value] || 'default';
        return (
          <Chip
            label={statusName}
            color={chipColor}
            size="small"
          />
        );
      },
    },
    {
      key: 'connectionStatus',
      label: t('reports.device.table.connectionStatus'),
      sortable: true,
      render: (value) => {
        const connStatus = connectionStatuses.find((s) => s.id === value);
        const connStatusName = connStatus?.name || value;
        // Color mapping: 0=Offline(error), 1=Online(success), 2=LostKey(warning), 3=Error(error), 4=Maintenance(warning), 5=Revoked(error)
        const colorMap = {
          0: 'error',    // Offline
          1: 'success',  // Online
          2: 'warning',  // LostKey
          3: 'error',    // Error
          4: 'warning',  // Maintenance
          5: 'error',    // Revoked
        };
        const chipColor = colorMap[value] || 'default';
        return (
          <Chip
            label={connStatusName}
            color={chipColor}
            size="small"
          />
        );
      },
    },
    {
      key: 'lastComunicationTime',
      label: t('reports.device.table.lastCommunicationTime'),
      sortable: true,
      render: (value) => (value ? formatDateTimeWithCulture(value, i18n.language) : '-'),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header Section */}
      <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AssessmentIcon fontSize="small" />
            <Typography variant="body2">{t('sidebar.module6.title')}</Typography>
          </Box>
          <Typography variant="body2" color="text.primary">
            {t('sidebar.module6.deviceReport')}
          </Typography>
        </Breadcrumbs>

        {/* Page Title */}
        <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left', mb: 3 }}>
          {t('reports.device.title')}
        </Typography>

        {/* Filter Section */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <Box sx={{ minWidth: 250 }}>
            <Select
              label={t('reports.device.filters.unit')}
              value={filterUnitId}
              onChange={(e) => dispatch(setFilterUnitId(e.target.value))}
              options={units}
              id="filterUnitId"
              name="filterUnitId"
              data-testid="deviceReport-select-filterUnitId"
              fullWidth={true}
              required
            />
          </Box>
          <Box sx={{ minWidth: 300 }}>
            <DateRangePicker
              label={t('reports.device.filters.dateRange')}
              fromValue={filterFromDate}
              toValue={filterToDate}
              onFromChange={(e) => dispatch(setFilterFromDate(e.target.value))}
              onToChange={(e) => dispatch(setFilterToDate(e.target.value))}
              id="filterDateRange"
              name="filterDateRange"
              data-testid="deviceReport-daterangepicker-filter"
            />
          </Box>
          <Button
            variant="primary"
            icon="fas fa-search"
            iconPosition="left"
            onClick={handleSearch}
            disabled={loading || isEmpty(filterUnitId) || isEmpty(filterFromDate) || isEmpty(filterToDate)}
            id="search"
            name="search"
            data-testid="deviceReport-button-search"
            size="lg"
          >
            {t('reports.device.search')}
          </Button>
          <ExportButton
            loading={exportLoading}
            disabled={isEmpty(filterUnitId) || isEmpty(filterFromDate) || isEmpty(filterToDate)}
            onClick={handleExportExcel}
            id="exportExcel"
            name="exportExcel"
            data-testid="deviceReport-button-exportExcel"
          />
          <ExportButton
            loading={pdfExportLoading}
            disabled={isEmpty(filterUnitId) || isEmpty(filterFromDate) || isEmpty(filterToDate)}
            onClick={handleExportPdf}
            id="exportPdf"
            name="exportPdf"
            data-testid="deviceReport-button-exportPdf"
            type="pdf"
          />
          <Button
            variant="secondary"
            icon="fas fa-sync-alt"
            iconPosition="left"
            onClick={handleRefreshData}
            loading={refreshLoading}
            disabled={refreshLoading || isEmpty(filterUnitId) || filterUnitId === 0 || filterUnitId === '0'}
            id="refreshData"
            name="refreshData"
            data-testid="deviceReport-button-refreshData"
            size="lg"
          >
            {t('reports.device.refreshData')}
          </Button>
          <Button
            variant="outline"
            icon="close"
            iconPosition="left"
            onClick={handleClearFilters}
            disabled={loading}
            id="clearFilters"
            name="clearFilters"
            data-testid="deviceReport-button-clearFilters"
            size="lg"
          >
            {t('reports.filters.clear')}
          </Button>
        </Box>
      </Box>

      {/* Dashboard Section */}
      {reportData && dashboardStats && (
        <Box sx={{ px: 3, py: 2, backgroundColor: '#f5f5f5', flex: 1, overflow: 'auto' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t('reports.device.dashboard.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {(() => {
              const selectedUnit = units.find((u) => u.id === Number(filterUnitId));
              const unitName = selectedUnit?.name || reportData.unitName || filterUnitId;
              const formattedFromDate = formatDateWithCulture(filterFromDate, i18n.language);
              const formattedToDate = formatDateWithCulture(filterToDate, i18n.language);
              return t('reports.device.dashboard.subtitleRange', { unit: unitName, fromDate: formattedFromDate, toDate: formattedToDate });
            })()}
          </Typography>

          {/* Statistics Cards (5 boxes) */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            {dashboardStats.map((stat) => (
              <Box
                key={stat.key}
                sx={{
                  flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(20% - 13px)' },
                  minWidth: { xs: '100%', sm: '200px', md: '180px' },
                }}
              >
                <Card
                  sx={{
                    backgroundColor: stat.bgColor,
                    borderLeft: `4px solid ${stat.color}`,
                    height: '100%',
                  }}
                >
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', flex: 1, textAlign: 'left' }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 1, lineHeight: 1.4, minHeight: '40px', display: 'flex', alignItems: 'center' }}
                        >
                          {stat.label}
                        </Typography>
                        <Typography variant="h4" component="div" sx={{ fontWeight: 700, lineHeight: 1.2, color: stat.color }}>
                          {stat.value.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ color: stat.color, ml: 2, flexShrink: 0 }}>{stat.icon}</Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>

          {/* Device Table */}
          {reportData.dataDeviceInDay && reportData.dataDeviceInDay.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t('reports.device.table.title')}
              </Typography>

              {/* Local Table Filters */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexWrap: 'wrap',
                  alignItems: 'flex-end',
                  mb: 2,
                  p: 2,
                  backgroundColor: '#ffffff',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ minWidth: 200 }}>
                  <Select
                    label={t('reports.device.tableFilters.deviceType')}
                    value={filterTableDeviceTypes}
                    onChange={(values) => dispatch(setFilterTableDeviceTypes(values))}
                    options={deviceSettingTypes}
                    id="filterTableDeviceTypes"
                    name="filterTableDeviceTypes"
                    data-testid="deviceReport-select-filterTableDeviceTypes"
                    fullWidth={true}
                    multiple
                  />
                </Box>
                <Box sx={{ minWidth: 200 }}>
                  <Select
                    label={t('reports.device.tableFilters.status')}
                    value={filterTableStatuses}
                    onChange={(values) => dispatch(setFilterTableStatuses(values))}
                    options={deviceStatuses}
                    id="filterTableStatuses"
                    name="filterTableStatuses"
                    data-testid="deviceReport-select-filterTableStatuses"
                    fullWidth={true}
                    multiple
                  />
                </Box>
                <Box sx={{ minWidth: 200 }}>
                  <Select
                    label={t('reports.device.tableFilters.connectionStatus')}
                    value={filterTableConnectionStatuses}
                    onChange={(values) => dispatch(setFilterTableConnectionStatuses(values))}
                    options={connectionStatuses}
                    id="filterTableConnectionStatuses"
                    name="filterTableConnectionStatuses"
                    data-testid="deviceReport-select-filterTableConnectionStatuses"
                    fullWidth={true}
                    multiple
                  />
                </Box>
                <Box sx={{ minWidth: 300 }}>
                  <DateRangePicker
                    label={t('reports.device.tableFilters.lastCommDateRange')}
                    fromValue={filterLastCommFromDate}
                    toValue={filterLastCommToDate}
                    onFromChange={(e) => dispatch(setFilterLastCommFromDate(e.target.value))}
                    onToChange={(e) => dispatch(setFilterLastCommToDate(e.target.value))}
                    id="filterLastCommDateRange"
                    name="filterLastCommDateRange"
                    data-testid="deviceReport-daterangepicker-filterLastComm"
                  />
                </Box>
                <Button
                  variant="outline"
                  icon="close"
                  iconPosition="left"
                  onClick={handleClearTableFilters}
                  id="clearTableFilters"
                  name="clearTableFilters"
                  data-testid="deviceReport-button-clearTableFilters"
                  size="lg"
                >
                  {t('reports.device.tableFilters.clear')}
                </Button>
              </Box>

              <Table
                id="deviceReport-table"
                columns={columns}
                data={filteredAndSortedData}
                loading={loading}
                pagination={true}
                sortable={true}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Empty State */}
      {!reportData && !loading && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
          }}
        >
          <DevicesOtherIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('reports.device.noData')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('reports.device.selectFiltersToSearch')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default DeviceReport;
